import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Radio, RotateCcw, TriangleAlert, Zap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HelpHint } from '../components/HelpHint';
import { PageNav } from '../components/PageNav';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import type * as T from '../lib/types';

const VERDICT_TONE: Record<string, string> = {
  undesigned: 'text-muted-foreground bg-muted border-border',
  ok: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  tight: 'text-amber-700 bg-amber-50 border-amber-200',
  fails: 'text-danger-700 bg-danger-50 border-danger-200',
  overloaded: 'text-orange-700 bg-orange-50 border-orange-200',
};

const VERDICT_STROKE: Record<string, string> = {
  // Grey, and quiet. An undescribed path is not a fault.
  undesigned: '#cbd5e1',
  ok: '#059669',
  tight: '#b45309',
  fails: '#b91c1c',
  overloaded: '#c2410c',
};

/** What each kind of element looks like. Shape carries meaning before colour. */
const NODE = {
  olt: { w: 132, h: 44, fill: '#0B2B5B', label: 'OLT' },
  pop: { w: 132, h: 44, fill: '#0B2B5B', label: 'POP' },
  span: { w: 108, h: 34, fill: '#475569', label: 'Span' },
  switchbox: { w: 116, h: 38, fill: '#334155', label: 'Switch' },
  fat: { w: 116, h: 38, fill: '#1d4ed8', label: 'FAT' },
  bts: { w: 116, h: 38, fill: '#7c3aed', label: 'BTS' },
  cpe: { w: 100, h: 32, fill: '#0f766e', label: 'CPE' },
  ont: { w: 100, h: 32, fill: '#0f766e', label: 'ONT' },
} as const;

const shapeFor = (type: string) => NODE[type as keyof typeof NODE] ?? NODE.span;

/**
 * The network, as something you can lay out and test.
 *
 * ## Why this is not Packet Tracer
 *
 * Packet Tracer simulates routers and switches so somebody can study for a
 * CCNA. GoSpeedNet runs fibre, and the question that costs money is not "will
 * this packet route" but whether the light reaches the far end. One splitter
 * too many, three hundred metres more than planned, a fourth splice on a drop —
 * and the customer sees nothing. That failure is invisible on a diagram and
 * obvious in decibels.
 *
 * So this simulates the thing that actually goes wrong here: place the plant,
 * give each hop its distance and split, and every endpoint is coloured by
 * whether it will work. Green has headroom, amber closes by almost nothing,
 * red does not close at all.
 *
 * ## Laid out automatically until somebody moves something
 *
 * An element with no saved position is placed by depth and order — roots at the
 * top, customers at the bottom. Dragging one saves where you put it, and
 * everything else stays where the layout put it. That means a network that has
 * never been arranged is still readable on first open, which a blank canvas
 * with a hundred elements piled at the origin would not be.
 */
export function NetworkDesignPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const surface = useRef<SVGSVGElement>(null);

  const [selected, setSelected] = useState<string | null>(null);
  const [dragging, setDragging] = useState<{ id: string; dx: number; dy: number } | null>(null);
  /** Positions being dragged right now, before they are saved. */
  const [local, setLocal] = useState<Record<string, { x: number; y: number }>>({});
  const [optic, setOptic] = useState<'B+' | 'C+'>('B+');

  const design = useQuery({
    queryKey: ['network-design', optic],
    queryFn: () => api.networkDesign(optic),
  });

  const move = useMutation({
    mutationFn: (vars: { id: string; x: number; y: number }) =>
      api.moveNetworkElement(vars.id, vars.x, vars.y),
  });

  const setFigures = useMutation({
    mutationFn: (vars: { id: string; figures: Record<string, number> }) =>
      api.setNetworkFigures(vars.id, vars.figures),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['network-design'] }),
  });

  const elements = useMemo(() => design.data?.elements ?? [], [design.data]);

  /**
   * Where everything sits.
   *
   * Saved positions win; anything unplaced is laid out by its depth in the
   * tree. Computed once per data change rather than on every render — with a
   * drag in progress this runs sixty times a second otherwise.
   */
  const placed = useMemo(() => {
    const byId = new Map(elements.map((e) => [e.id, e]));
    const depthOf = (element: T.DesignElement): number => {
      let depth = 0;
      let current: T.DesignElement | undefined = element;
      const seen = new Set<string>();
      while (current?.parentId && !seen.has(current.id)) {
        seen.add(current.id);
        current = byId.get(current.parentId);
        depth += 1;
      }
      return depth;
    };

    const rows = new Map<number, T.DesignElement[]>();
    for (const element of elements) {
      const depth = depthOf(element);
      rows.set(depth, [...(rows.get(depth) ?? []), element]);
    }

    const positions: Record<string, { x: number; y: number }> = {};
    for (const [depth, row] of rows) {
      row.forEach((element, index) => {
        positions[element.id] = {
          x: element.x ?? 90 + index * 165,
          y: element.y ?? 70 + depth * 110,
        };
      });
    }
    return positions;
  }, [elements]);

  const positionOf = useCallback(
    (id: string) => local[id] ?? placed[id] ?? { x: 0, y: 0 },
    [local, placed],
  );

  /*
   * Drag handled on the surface rather than on each node.
   *
   * A pointermove bound to every node stops firing the moment the cursor
   * outruns the shape, which on a fast drag is immediately — the node sticks
   * and then jumps. One listener on the SVG follows the pointer wherever it
   * goes.
   */
  useEffect(() => {
    if (!dragging) return;

    const onMove = (event: PointerEvent) => {
      const box = surface.current?.getBoundingClientRect();
      if (!box) return;
      setLocal((current) => ({
        ...current,
        [dragging.id]: {
          x: Math.round(event.clientX - box.left - dragging.dx),
          y: Math.round(event.clientY - box.top - dragging.dy),
        },
      }));
    };

    const onUp = () => {
      const final = local[dragging.id];
      if (final) move.mutate({ id: dragging.id, ...final });
      setDragging(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragging, local, move]);

  const budgetFor = (id: string) => design.data?.budgets.find((b) => b.elementId === id);
  const chosen = selected ? elements.find((e) => e.id === selected) : null;
  const chosenBudget = selected ? budgetFor(selected) : null;

  const counts = design.data?.counts;
  const extent = Object.values(placed).reduce(
    (max, p) => ({ x: Math.max(max.x, p.x + 200), y: Math.max(max.y, p.y + 140) }),
    { x: 900, y: 500 },
  );

  return (
    <div className="space-y-4">
      <PageNav crumbs={[{ label: 'Network', to: '/network' }, { label: 'Design' }]} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-1.5 font-heading text-2xl">
            Network design
            <HelpHint>
              Every customer is coloured by whether the light actually reaches them. Give each hop
              its distance and split ratio and the figures follow — this is the fibre equivalent of
              running a packet through and seeing whether it arrives.
            </HelpHint>
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Drag to arrange. Click anything to see what it costs in decibels and what a break there
            would take out.
          </p>
        </div>

        <label className="text-sm">
          Optics
          <select
            className="input mt-1"
            value={optic}
            onChange={(event) => setOptic(event.target.value as 'B+' | 'C+')}
          >
            <option value="B+">Class B+ (standard)</option>
            <option value="C+">Class C+ (long reach)</option>
          </select>
        </label>
      </div>

      {/* ── How the plant is doing ─────────────────────────────────────────── */}
      {counts && (
        <div className="flex flex-wrap gap-2 text-sm">
          {(
            [
              ['undesigned', 'Not described yet', counts.undesigned],
              ['ok', 'Fine', counts.ok],
              ['tight', 'Only just', counts.tight],
              ['fails', 'Will not work', counts.fails],
              ['overloaded', 'Too much light', counts.overloaded],
            ] as const
          )
            .filter(([, , n]) => n > 0)
            .map(([verdict, label, n]) => (
              <span key={verdict} className={`badge border ${VERDICT_TONE[verdict]}`}>
                {n} {label}
              </span>
            ))}
        </div>
      )}

      {design.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Working out the light budget
        </p>
      )}

      {design.data && elements.length === 0 && (
        <p className="rounded-xl border border-border bg-muted/40 px-4 py-8 text-center text-sm text-muted-foreground">
          Nothing in the network yet. Add a POP and hang elements off it on the Network screen, then
          come back here to lay it out and test it.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        {/* ── The canvas ───────────────────────────────────────────────────── */}
        {elements.length > 0 && (
          <div className="overflow-auto rounded-xl border border-border bg-card">
            <svg
              ref={surface}
              width={extent.x}
              height={extent.y}
              className="touch-none select-none"
              role="img"
              aria-label="Network design canvas"
            >
              {/* Links first, so nodes sit on top of them. */}
              {elements.map((element) => {
                if (!element.parentId) return null;
                const from = positionOf(element.parentId);
                const to = positionOf(element.id);
                const budget = budgetFor(element.id);
                const parentShape = shapeFor(
                  elements.find((e) => e.id === element.parentId)?.type ?? 'span',
                );
                const shape = shapeFor(element.type);

                return (
                  <line
                    key={`link-${element.id}`}
                    x1={from.x + parentShape.w / 2}
                    y1={from.y + parentShape.h}
                    x2={to.x + shape.w / 2}
                    y2={to.y}
                    stroke={budget ? VERDICT_STROKE[budget.verdict] : '#cbd5e1'}
                    strokeWidth={budget?.verdict === 'fails' ? 2.5 : 1.5}
                    // A dashed line for a link that does not close, so the
                    // failure survives being printed in black and white.
                    strokeDasharray={budget?.verdict === 'fails' ? '5 3' : undefined}
                  />
                );
              })}

              {elements.map((element) => {
                const at = positionOf(element.id);
                const shape = shapeFor(element.type);
                const budget = budgetFor(element.id);
                const isSelected = selected === element.id;

                return (
                  <g
                    key={element.id}
                    transform={`translate(${at.x}, ${at.y})`}
                    className={can('network:write') ? 'cursor-move' : 'cursor-pointer'}
                    onPointerDown={(event) => {
                      setSelected(element.id);
                      if (!can('network:write')) return;
                      const box = surface.current?.getBoundingClientRect();
                      if (!box) return;
                      setDragging({
                        id: element.id,
                        dx: event.clientX - box.left - at.x,
                        dy: event.clientY - box.top - at.y,
                      });
                    }}
                  >
                    <rect
                      width={shape.w}
                      height={shape.h}
                      rx={9}
                      fill={shape.fill}
                      stroke={
                        isSelected
                          ? '#FF6B00'
                          : budget
                            ? VERDICT_STROKE[budget.verdict]
                            : 'transparent'
                      }
                      strokeWidth={isSelected ? 3 : 2}
                    />
                    <text
                      x={shape.w / 2}
                      y={shape.h / 2 - 2}
                      textAnchor="middle"
                      fill="white"
                      fontSize="11"
                      fontWeight="600"
                    >
                      {element.name.length > 16 ? `${element.name.slice(0, 15)}…` : element.name}
                    </text>
                    <text
                      x={shape.w / 2}
                      y={shape.h / 2 + 11}
                      textAnchor="middle"
                      fill="rgba(255,255,255,0.75)"
                      fontSize="9"
                    >
                      {budget ? `${budget.receivedDbm} dBm` : shape.label}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        )}

        {/* ── What is selected ─────────────────────────────────────────────── */}
        {elements.length > 0 && (
          <aside className="space-y-3">
            {!chosen && (
              <p className="rounded-xl border border-border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
                Click anything on the canvas.
              </p>
            )}

            {chosen && (
              <>
                <div className="card">
                  <h2 className="font-heading text-lg">{chosen.name}</h2>
                  <p className="text-xs label-mono tracking-[0.12em] text-muted-foreground">
                    {chosen.type}
                  </p>

                  {chosenBudget && (
                    <div
                      className={`mt-3 rounded-xl border px-3 py-2 text-sm ${VERDICT_TONE[chosenBudget.verdict]}`}
                    >
                      <p className="flex items-center gap-1.5 font-medium">
                        {chosenBudget.verdict === 'ok' ? (
                          <Zap className="h-3.5 w-3.5" aria-hidden="true" />
                        ) : (
                          <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        {chosenBudget.receivedDbm} dBm at the far end
                      </p>
                      <p className="mt-1 text-xs">{chosenBudget.summary}</p>
                    </div>
                  )}

                  {chosenBudget && (
                    <dl className="mt-3 space-y-1 text-xs">
                      {[
                        ['Total loss', `${chosenBudget.totalLossDb} dB`],
                        ['Fibre run', `${chosenBudget.totalMetres} m`],
                        ['Split', `1:${chosenBudget.splitWays}`],
                        ['Margin left', `${chosenBudget.marginDb} dB`],
                      ].map(([label, value]) => (
                        <div key={label} className="flex justify-between gap-2">
                          <dt className="text-muted-foreground">{label}</dt>
                          <dd className="font-medium tabular-nums">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>

                {can('network:write') && (
                  <Figures
                    element={chosen}
                    saving={setFigures.isPending}
                    onSave={(figures) => setFigures.mutate({ id: chosen.id, figures })}
                  />
                )}

                <Downstream elementId={chosen.id} />
              </>
            )}
          </aside>
        )}
      </div>

      {Object.keys(local).length > 0 && (
        <button
          type="button"
          className="btn-quiet text-xs"
          onClick={() => {
            setLocal({});
            void queryClient.invalidateQueries({ queryKey: ['network-design'] });
          }}
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Reload the saved layout
        </button>
      )}
    </div>
  );
}

/** The figures the budget is computed from. */
function Figures({
  element,
  saving,
  onSave,
}: {
  element: T.DesignElement;
  saving: boolean;
  onSave: (figures: Record<string, number>) => void;
}) {
  const [metres, setMetres] = useState(String(element.metres ?? ''));
  const [splitRatio, setSplit] = useState(String(element.splitRatio ?? ''));
  const [splices, setSplices] = useState(String(element.splices ?? ''));
  const [connectors, setConnectors] = useState(String(element.connectors ?? ''));

  // Reset when a different element is clicked, or the boxes keep the last one's
  // numbers and look like they belong to this one.
  useEffect(() => {
    setMetres(String(element.metres ?? ''));
    setSplit(String(element.splitRatio ?? ''));
    setSplices(String(element.splices ?? ''));
    setConnectors(String(element.connectors ?? ''));
  }, [element.id, element.metres, element.splitRatio, element.splices, element.connectors]);

  const numeric = (value: string): number | undefined => {
    if (!value.trim()) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  return (
    <form
      className="card space-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        const figures: Record<string, number> = {};
        const metresValue = numeric(metres);
        const splitValue = numeric(splitRatio);
        const spliceValue = numeric(splices);
        const connectorValue = numeric(connectors);
        if (metresValue !== undefined) figures.metres = metresValue;
        if (splitValue !== undefined) figures.splitRatio = splitValue;
        if (spliceValue !== undefined) figures.splices = spliceValue;
        if (connectorValue !== undefined) figures.connectors = connectorValue;
        onSave(figures);
      }}
    >
      <h3 className="font-heading text-sm">The figures</h3>

      <label className="block text-xs">
        Fibre into this element (metres)
        <input
          className="input mt-0.5 tabular-nums"
          inputMode="numeric"
          value={metres}
          onChange={(event) => setMetres(event.target.value)}
          placeholder="0"
        />
      </label>

      <label className="block text-xs">
        Splitter
        <select
          className="input mt-0.5"
          value={splitRatio}
          onChange={(event) => setSplit(event.target.value)}
        >
          <option value="">None</option>
          {[2, 4, 8, 16, 32, 64].map((ratio) => (
            <option key={ratio} value={ratio}>
              1:{ratio}
            </option>
          ))}
        </select>
      </label>

      <div className="flex gap-2">
        <label className="flex-1 text-xs">
          Splices
          <input
            className="input mt-0.5 tabular-nums"
            inputMode="numeric"
            value={splices}
            onChange={(event) => setSplices(event.target.value)}
            placeholder="0"
          />
        </label>
        <label className="flex-1 text-xs">
          Connectors
          <input
            className="input mt-0.5 tabular-nums"
            inputMode="numeric"
            value={connectors}
            onChange={(event) => setConnectors(event.target.value)}
            placeholder="0"
          />
        </label>
      </div>

      <button type="submit" className="btn-secondary w-full text-sm" disabled={saving}>
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
        Recalculate
      </button>
    </form>
  );
}

/** What a break here would take out. */
function Downstream({ elementId }: { elementId: string }) {
  const affected = useQuery({
    queryKey: ['network-downstream', elementId],
    queryFn: () => api.networkDownstream(elementId),
  });

  if (!affected.data) return null;

  return (
    <div className="card">
      <h3 className="flex items-center gap-1.5 font-heading text-sm">
        <Radio className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        If this failed
      </h3>
      <p className="mt-1 text-xs text-muted-foreground">{affected.data.summary}</p>

      {affected.data.elements.length > 0 && (
        <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs">
          {affected.data.elements.map((element) => (
            <li key={element.id} className="flex justify-between gap-2">
              <span className="truncate">{element.name}</span>
              <span className="shrink-0 text-muted-foreground">{element.type}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
